import { Component, OnInit, Inject } from '@angular/core';
import { MatLegacyDialogRef as MatDialogRef, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/legacy-dialog';

import * as _ from 'lodash';

import { MessagePollCreateBody } from '../../../../../models/message-poll-body';
import { TnNotificationService } from '../../../../../utilities/tn-notification/tn-notification.service';
import { TimestampService } from '../../../../../utilities/timestamp/timestamp.service';

const POLL_OPTION_MIN_COUNT = 2;
const POLL_OPTION_MAX_COUNT = 12;
const POLL_TITLE_MAX_LENGTH = 255;
const POLL_OPTION_MAX_LENGTH = 100;

export const PollShouldDisplayResult = {
  NEVER: 0,
  ALWAYS: 1,
  AFTER_VOTE_END: 2,
  AFTER_USER_VOTE: 3
};

@Component({
  selector: 'tn-poll-create',
  templateUrl: './poll-create.component.html',
  styleUrls: ['./poll-create.component.scss']
})
export class PollCreateComponent implements OnInit {

  POLL_OPTION_MIN_COUNT = POLL_OPTION_MIN_COUNT;
  POLL_OPTION_MAX_COUNT = POLL_OPTION_MAX_COUNT;
  POLL_TITLE_MAX_LENGTH = POLL_TITLE_MAX_LENGTH;
  POLL_OPTION_MAX_LENGTH = POLL_OPTION_MAX_LENGTH;
  PollShouldDisplayResult = PollShouldDisplayResult;

  title: string = '';
  options: string[] = ['', ''];
  // VTGT-1748 asks for this on by default; the server's own default when the field is
  // omitted is 0, so the value is always sent explicitly.
  allowMultipleChoice: boolean = true;
  shouldDisplayResult: number = PollShouldDisplayResult.ALWAYS;
  isSetVoteEnd: boolean = false;
  voteEndInput: string = null; // bound to <input type="datetime-local">

  shouldDisplayResultOptions = [
    { value: PollShouldDisplayResult.NEVER, labelKey: 'WEBCLIENT.CHATROOM.POLL.DISPLAY_RESULT_OPTIONS.NEVER' },
    { value: PollShouldDisplayResult.ALWAYS, labelKey: 'WEBCLIENT.CHATROOM.POLL.DISPLAY_RESULT_OPTIONS.ALWAYS' },
    { value: PollShouldDisplayResult.AFTER_VOTE_END, labelKey: 'WEBCLIENT.CHATROOM.POLL.DISPLAY_RESULT_OPTIONS.AFTER_VOTE_END' },
    { value: PollShouldDisplayResult.AFTER_USER_VOTE, labelKey: 'WEBCLIENT.CHATROOM.POLL.DISPLAY_RESULT_OPTIONS.AFTER_USER_VOTE' }
  ];

  constructor(
    public dialogRef: MatDialogRef<PollCreateComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private _tnNotificationService: TnNotificationService,
    private _timestampService: TimestampService
  ) { }

  ngOnInit() {
    this.dialogRef.updateSize('50vw');
  }

  get showAfterVoteEndHint(): boolean {
    return this.shouldDisplayResult === PollShouldDisplayResult.AFTER_VOTE_END && !this.getVoteEndInput();
  }

  /**
   * The typed end time only counts while the toggle is on, so turning it off reverts the poll to
   * open-ended without the user having to clear the picker.
   */
  getVoteEndInput(): string {
    return this.isSetVoteEnd ? this.voteEndInput : null;
  }

  onToggleVoteEnd(): void {
    if (!this.isSetVoteEnd) {
      this.voteEndInput = null;
    }
  }

  trackByIndex(index: number): number {
    return index;
  }

  addOption(): void {
    if (this.options.length >= POLL_OPTION_MAX_COUNT) {
      return;
    }
    this.options.push('');
  }

  /**
   * Reorder before sending (VTGT-1748). Swapping values in place keeps the inputs themselves
   * stable, which matters because they are tracked by index.
   */
  moveOption(index: number, offset: number): void {
    let target = index + offset;
    if (target < 0 || target >= this.options.length) {
      return;
    }
    let moved = this.options[index];
    this.options[index] = this.options[target];
    this.options[target] = moved;
  }

  removeOption(index: number): void {
    if (this.options.length <= POLL_OPTION_MIN_COUNT) {
      return;
    }
    this.options.splice(index, 1);
  }

  cancel(): void {
    this.dialogRef.close();
  }

  confirm(): void {
    let title = _.trim(this.title);
    if (!title) {
      this._tnNotificationService.showCustomWarningByTranslateKey('WEBCLIENT.CHATROOM.POLL.ERROR.TITLE_REQUIRED');
      return;
    }
    if (title.length > POLL_TITLE_MAX_LENGTH) {
      this._tnNotificationService.showCustomWarningByTranslateKey('WEBCLIENT.CHATROOM.POLL.ERROR.TITLE_TOO_LONG');
      return;
    }

    let trimmedOptions = _.map(this.options, (o) => _.trim(o));
    let nonBlankOptions = _.filter(trimmedOptions, (o) => !!o);

    if (nonBlankOptions.length < POLL_OPTION_MIN_COUNT) {
      this._tnNotificationService.showCustomWarningByTranslateKey('WEBCLIENT.CHATROOM.POLL.ERROR.OPTIONS_MIN');
      return;
    }
    if (nonBlankOptions.length > POLL_OPTION_MAX_COUNT) {
      this._tnNotificationService.showCustomWarningByTranslateKey('WEBCLIENT.CHATROOM.POLL.ERROR.OPTIONS_MAX');
      return;
    }
    if (nonBlankOptions.length !== trimmedOptions.length) {
      this._tnNotificationService.showCustomWarningByTranslateKey('WEBCLIENT.CHATROOM.POLL.ERROR.OPTION_EMPTY');
      return;
    }
    if (_.some(nonBlankOptions, (o) => o.length > POLL_OPTION_MAX_LENGTH)) {
      this._tnNotificationService.showCustomWarningByTranslateKey('WEBCLIENT.CHATROOM.POLL.ERROR.OPTION_TOO_LONG');
      return;
    }

    let voteEnd: number = null;
    let voteEndInput = this.getVoteEndInput();
    if (voteEndInput) {
      let voteEndDate = new Date(voteEndInput);
      if (isNaN(voteEndDate.getTime())) {
        this._tnNotificationService.showCustomWarningByTranslateKey('WEBCLIENT.CHATROOM.POLL.ERROR.END_TIME_INVALID');
        return;
      }
      voteEnd = _.toInteger(this._timestampService.dateObjToSecondString(voteEndDate));
      if (voteEnd <= _.toInteger(this._timestampService.getNowSecondString())) {
        this._tnNotificationService.showCustomWarningByTranslateKey('WEBCLIENT.CHATROOM.POLL.ERROR.END_TIME_PAST');
        return;
      }
    }

    let pollBody: MessagePollCreateBody = {
      title: title,
      options: nonBlankOptions,
      allow_multiple_choice: this.allowMultipleChoice ? 1 : 0,
      should_display_result: this.shouldDisplayResult,
      vote_end: voteEnd
    };

    if (this.data && this.data.createPoll) {
      this.data.createPoll(pollBody);
    }
    this.dialogRef.close();
  }

}
